import axios from 'axios';
import { StorageServices } from 'utils/storage';
import { ACCESS_TOKEN, HTTPS_CODE, REFRESH_TOKEN_KEY } from './constant';
import toast from 'react-hot-toast';
import LoginAPI from 'pages/Auth/services/api';
import { ROUTES } from 'routers/routes';

declare module 'axios' {
  interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

type FailedRequest = {
  resolve: (token: string) => void;
  reject: (error: any) => void;
};

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: any, token?: string) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = StorageServices.getData(ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Accept-Language'] = StorageServices.getData('i18nextLng');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  async (response) => {
    // Redirect to Login Page

    if (
      response.data.code === HTTPS_CODE.unauthorized ||
      response.data.code === HTTPS_CODE.lockedAccount
    ) {
      toast.error(response.data.message);

      setTimeout(() => {
        StorageServices.removeData(ACCESS_TOKEN);
        StorageServices.removeData(REFRESH_TOKEN_KEY);
        window.location.href = `${ROUTES.LOGIN}`;
      }, 500);
    }

    // Redirect to 403 page
    if (response.data.code === HTTPS_CODE.forbidden) {
      setTimeout(() => {
        window.location.href = '/403';
      }, 500);
    }

    // Refresh token
    if (response.data.code === HTTPS_CODE.expiredToken) {
      const originalRequest = response.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;
        const refreshToken = StorageServices.getData(REFRESH_TOKEN_KEY);

        if (!refreshToken) {
          StorageServices.removeData(ACCESS_TOKEN);
          StorageServices.removeData(REFRESH_TOKEN_KEY);
          window.location.href = ROUTES.LOGIN;
          return Promise.reject(new Error('Refresh token không tồn tại'));
        }

        try {
          const refreshResponse =
            await LoginAPI.handleRefreshToken(refreshToken);

          if (refreshResponse.code === HTTPS_CODE.expiredToken) {
            StorageServices.removeData(ACCESS_TOKEN);
            StorageServices.removeData(REFRESH_TOKEN_KEY);

            toast.error('Hết phiên đăng nhập, Vui lòng đăng nhập lại', {
              position: 'top-right',
            });

            setTimeout(() => {
              window.location.href = ROUTES.LOGIN;
            }, 500);
          }

          const newAccessToken = refreshResponse.data.token;
          StorageServices.setData(ACCESS_TOKEN, newAccessToken);
          axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        } catch (err) {
          processQueue(err);
          window.location.href = ROUTES.LOGIN;
          StorageServices.removeData(ACCESS_TOKEN);
          StorageServices.removeData(REFRESH_TOKEN_KEY);

          toast.error('Hết phiên đăng nhập, Vui lòng đăng nhập lại', {
            position: 'top-right',
          });

          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }
    }

    return response;
  },

  async (error) => {
    return Promise.reject(error);
  }
);

export default axiosInstance;
